import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { HELP_ITEMS, PACKAGES, FAQS, BANKING } from "../data/site";
import { api, apiError } from "../lib/api";

const design = {
  hero: "https://images.unsplash.com/photo-1765648580808-76d75e4f3833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxoYXBweSUyMHNvdXRoJTIwYWZyaWNhbiUyMHBlb3BsZSUyMHN0dWRlbnQlMjBob3VzZSUyMGNvbnN0cnVjdGlvbiUyMHNtYWxsJTIwYnVzaW5lc3MlMjBvd25lcnxlbnwwfHx8fDE3OTA3MzA4Mzl8MA&ixlib=rb-4.1.0&q=85",
};

function AccentPackage({ pkg, onJoin }) {
  const rings = {
    slate: "border-slate-200",
    blue: "border-blue-300",
    green: "border-emerald-500 ring-2 ring-emerald-500/20 shadow-lg",
    gold: "border-amber-400 ring-2 ring-amber-400/20",
  };
  const badgeColor = {
    slate: "bg-slate-100 text-slate-600",
    blue: "bg-blue-100 text-blue-700",
    green: "bg-emerald-600 text-white",
    gold: "bg-amber-400 text-[#0A192F]",
  };
  return (
    <div className={`relative bg-white rounded-2xl border p-6 flex flex-col ${rings[pkg.accent]}`} data-testid={`package-card-${pkg.id}`}>
      <span className={`self-start text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${badgeColor[pkg.accent]}`}>{pkg.badge}</span>
      <h3 className="mt-4 text-xl font-head font-bold text-[#0A192F]">{pkg.name}</h3>
      <p className="text-sm text-slate-500 mt-1">{pkg.target}</p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-3xl font-extrabold text-[#0A192F] font-head">{pkg.min}</span>
        <span className="text-sm text-slate-500">/ {pkg.period.replace("per ", "")}</span>
      </div>
      <p className="text-xs text-emerald-600 font-medium mt-1">minimum — you can always pay more</p>
      <p className="text-sm text-slate-600 mt-4 leading-relaxed flex-1">{pkg.desc}</p>
      <Button onClick={onJoin} data-testid={`package-join-${pkg.id}`} className="mt-6 bg-[#0A192F] hover:bg-[#12233f] text-white rounded-full">Choose {pkg.name.split(" ")[0]}</Button>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [amount, setAmount] = useState(200);
  const [months, setMonths] = useState(12);
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/site-content").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const tier = amount < 200 ? PACKAGES[0] : amount < 500 ? PACKAGES[1] : amount >= 1000 ? PACKAGES[3] : PACKAGES[2];
  const contributed = amount * months;
  const estHelp = contributed * (tier.id === "basic" ? 3 : tier.id === "standard" ? 4 : tier.id === "premium" ? 5 : 6);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message sent! We'll be in touch soon.");
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      {content.announcement_active && content.announcement && (
        <div className="bg-amber-400 text-[#0A192F] text-center text-sm font-medium py-2 px-4" data-testid="site-announcement">{content.announcement}</div>
      )}

      {/* Hero */}
      <section className="gdf-hero-glow text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="gdf-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5">
              <Icons.Sparkles className="w-3.5 h-3.5" /> No limits · No fine print
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-head font-extrabold leading-tight tracking-tight">
              {content.hero_headline || (<>Where banks say <span className="text-red-400">NO</span>,<br />we say <span className="gdf-gradient-text">YES</span>.</>)}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              {content.hero_subtext || "GIVERS DRIVEN FUNDS helps you with anything — loans, school fees, medical bills, building your home, starting a business, or a little petty cash. Contribute monthly, and when you need help, you get a YES."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/register")} data-testid="hero-join-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full px-8 h-12 text-base">
                Become a member <Icons.ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button onClick={() => document.getElementById("packages")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-packages-btn" variant="outline" className="rounded-full px-8 h-12 text-base border-white/30 text-white bg-white/5 hover:bg-white/10">
                See packages
              </Button>
            </div>
            <div className="mt-10 flex gap-8">
              {[["Always", "A YES answer"], ["6 months", "Then request help"], ["R10", "Start today"]].map(([n, l]) => (
                <div key={l}>
                  <p className="text-2xl font-extrabold font-head text-amber-400">{n}</p>
                  <p className="text-xs text-slate-400">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative gdf-fade-up" style={{ animationDelay: "0.15s" }}>
            <img src={design.hero} alt="Community members" className="rounded-3xl object-cover w-full h-[420px] gdf-card-shadow" />
            <div className="absolute -bottom-6 -left-6 bg-white text-[#0A192F] rounded-2xl p-4 shadow-xl max-w-[220px] hidden sm:block">
              <div className="flex items-center gap-2 text-emerald-600 font-bold"><Icons.HandHeart className="w-5 h-5" /> Community fund</div>
              <p className="text-xs text-slate-500 mt-1">Your contributions become a safety net that's always there for you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Help grid */}
      <section id="help" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-emerald-600 font-bold">What we help with</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold text-[#0A192F]">There is no small or big — everything is important</h2>
          <p className="mt-4 text-slate-600">Whatever your problem, individual or collective, we work with you towards a solution.</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HELP_ITEMS.map((item, i) => {
            const Icon = Icons[item.icon] || Icons.CircleHelp;
            return (
              <div key={item.title} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-400 hover:-translate-y-1 transition-all duration-300" data-testid={`help-item-${i}`}>
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                <h3 className="mt-4 font-head font-semibold text-[#0A192F]">{item.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="gdf-navy-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-bold">How it works</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold">Four simple steps to a YES</h2>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-4">
            {[
              { icon: "UserPlus", t: "1. Join", d: "Register and choose a package that fits what you can afford." },
              { icon: "CalendarCheck", t: "2. Contribute monthly", d: "Pay your amount into our FNB account for at least 6 months." },
              { icon: "TrendingUp", t: "3. Build your record", d: "Keep a good payment record — the more you pay, the more you can get." },
              { icon: "CheckCircle2", t: "4. Get assistance", d: "Request help and get a YES, as long as the fund has balance." },
            ].map((s, i) => {
              const Icon = Icons[s.icon];
              return (
                <div key={s.t} className="relative" data-testid={`how-step-${i}`}>
                  <div className="h-12 w-12 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><Icon className="w-6 h-6" /></div>
                  <h3 className="mt-4 font-head font-semibold text-lg">{s.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculator + Packages */}
      <section id="packages" className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-emerald-600 font-bold">Membership packages</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold text-[#0A192F]">Choose your package — pay the minimum or more</h2>
          <p className="mt-4 text-slate-600">Minimums are just the start. Pay a higher amount and the system automatically upgrades your package for bigger help.</p>
        </div>

        <div className="mt-10 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 gdf-card-shadow max-w-3xl mx-auto">
          <p className="font-head font-semibold text-[#0A192F] mb-4">Estimate your assistance</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-slate-600">Monthly contribution (R)</label>
              <Input type="number" min={10} value={amount} onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))} data-testid="calc-amount" className="mt-1" />
            </div>
            <div>
              <label className="text-sm text-slate-600">Months contributing</label>
              <Input type="number" min={1} value={months} onChange={(e) => setMonths(Math.max(1, Number(e.target.value)))} data-testid="calc-months" className="mt-1" />
            </div>
          </div>
          <div className="mt-6 grid sm:grid-cols-3 gap-4 text-center">
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs text-slate-500">Your tier</p><p className="font-head font-bold text-[#0A192F] mt-1">{tier.name}</p></div>
            <div className="bg-slate-50 rounded-xl p-4"><p className="text-xs text-slate-500">Total contributed</p><p className="font-head font-bold text-[#0A192F] mt-1">R{contributed.toLocaleString()}</p></div>
            <div className="bg-emerald-50 rounded-xl p-4"><p className="text-xs text-emerald-700">Estimated help</p><p className="font-head font-bold text-emerald-700 mt-1" data-testid="calc-result">R{estHelp.toLocaleString()}</p></div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3 text-center">Illustrative only. Actual assistance depends on your record and available fund balance.</p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PACKAGES.map((pkg) => (
            <AccentPackage key={pkg.id} pkg={pkg} onJoin={() => navigate(`/register?package=${pkg.id}`)} />
          ))}
        </div>

        {/* Banking details */}
        <div className="mt-12 bg-[#0A192F] text-white rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-8" data-testid="banking-details">
          <div>
            <h3 className="font-head font-bold text-xl">Banking details</h3>
            <p className="text-slate-400 text-sm mt-2">Pay by EFT or deposit at any FNB branch, then upload your proof of payment in your dashboard.</p>
            <div className="mt-4 inline-flex items-center gap-2 bg-amber-400/10 border border-amber-400/30 rounded-lg px-3 py-2 text-amber-300 text-sm">
              <Icons.Info className="w-4 h-4" /> Reference: {BANKING.reference_format}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[["Account name", BANKING.account_name], ["Bank", BANKING.bank_name], ["Account type", BANKING.account_type], ["Account number", BANKING.account_number], ["Branch code", BANKING.branch_code]].map(([k, v]) => (
              <div key={k} className="bg-white/5 rounded-lg p-3">
                <p className="text-[11px] text-slate-400 uppercase tracking-wide">{k}</p>
                <p className="font-mono-gdf font-semibold mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-8">
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-8 sm:p-12 text-white grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-100 font-bold">Ready to start?</p>
            <h2 className="mt-3 text-3xl font-head font-bold">Join today and turn your monthly savings into a safety net</h2>
            <p className="mt-4 text-emerald-50 leading-relaxed">Become a member in minutes, choose a package that fits your pocket, and know that when you need help, the community is there with a YES.</p>
            <Button onClick={() => navigate("/register")} data-testid="cta-join-btn" className="mt-6 bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 font-semibold">Become a member</Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["From R10", "Start small"], ["Always a YES", "No fine print"], ["6 months", "Then request help"], ["Your safety net", "Savings that give back"]].map(([t, d]) => (
              <div key={t} className="bg-white/10 rounded-xl p-4 backdrop-blur"><p className="font-head font-bold">{t}</p><p className="text-xs text-emerald-100 mt-1">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-emerald-600 font-bold">Questions</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-head font-bold text-[#0A192F]">Frequently asked questions</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-head font-semibold text-[#0A192F]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Contact */}
      <section className="max-w-3xl mx-auto px-6 pb-24">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 gdf-card-shadow">
          <h2 className="text-2xl font-head font-bold text-[#0A192F]">Send us a message</h2>
          <p className="text-slate-500 text-sm mt-1">Have a question? We'll get back to you.</p>
          <form onSubmit={submitContact} className="mt-6 space-y-4">
            <Input placeholder="Your name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" />
            <Input type="email" placeholder="Your email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
            <Textarea placeholder="How can we help?" required rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8">{sending ? "Sending..." : "Send message"}</Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
